import { AlertTriangleIcon, Mail01Icon } from '@langchain/untitled-ui-icons';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';

import { ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useSetUser, useUnlinkSocialLogin, useUser } from '@/hooks/useSwr';
import { TenantSchema } from '@/types/schema';
import { pluralize } from '@/utils/stringUtils';

import {
  UPDATE_EMAIL_KEY,
  useShowEmailChange,
} from './utils/useShowEmailChange';

function EmailChangeNotification({ setOpen }) {
  const { trigger, isMutating } = useSetUser();
  const { data } = useUser();
  const user = data?.data.user;
  return (
    <div className="flex flex-col items-stretch gap-4 text-sm">
      <div className="text-lg font-semibold">Email confirmation</div>
      <div className="flex flex-col gap-2">
        <div className="text-sm text-secondary">
          We've sent a confirmation email to{' '}
          <span className="font-semibold">{user?.email}</span> and{' '}
          <span className="font-semibold">{user?.new_email}</span>. To finish
          changing your email, please click the confirmation link in both
          emails.
        </div>

        <div className="text-sm text-secondary">
          Cancel the request to keep using{' '}
          <span className="font-semibold">{user?.email}</span>.
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          color="neutral"
          variant="outlined"
          onClick={() => setOpen(false)}
        >
          Close
        </Button>
        <Button
          color="danger"
          onClick={() =>
            trigger({
              data: {
                [UPDATE_EMAIL_KEY]: false,
              },
            })
          }
          loading={isMutating}
        >
          Cancel request
        </Button>
      </div>
    </div>
  );
}
function EmailEdit({ setOpen }: { setOpen: (value: boolean) => void }) {
  'use no memo';
  const { data } = useUser();
  const { trigger, isMutating } = useSetUser();
  const { trigger: unlinkSocialLogin, isMutating: isUnlinkingSocialLogin } =
    useUnlinkSocialLogin();

  const { user } = data?.data ?? {};

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setError,
  } = useForm<{ email: string }>({
    defaultValues: {
      email: user?.email ?? '',
    },
  });

  const socialLogins =
    user?.identities?.filter((id) => id.provider !== 'email') ?? [];

  const onSubmit = async ({ email }) => {
    try {
      await unlinkSocialLogin(socialLogins);
      await trigger({
        email,
        data: {
          [UPDATE_EMAIL_KEY]: true,
        },
      });
    } catch (e) {
      console.error(e);
      setError('email', {
        type: 'manual',
        message: (e as Error).message,
      });
    }
  };

  const hasError = !!errors.email;
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col justify-between gap-4"
    >
      <FormControl error={hasError}>
        <div className="text-lg font-semibold">Change email</div>
        {socialLogins.length > 0 && (
          <>
            <div className="my-2 flex gap-2 rounded-md bg-warning-primary p-4">
              <div className="mt-1">
                <AlertTriangleIcon className="h-4 w-4 text-warning" />
              </div>
              <div className="flex flex-col gap-2">
                <span className="font-semibold">
                  Warning: Linked SSO logins will be disconnected
                </span>
                <span className="mb-2 text-sm text-secondary">
                  Changing your email will remove your{' '}
                  <span className="font-semibold capitalize">
                    {socialLogins.map((id) => id.provider).join(', ')}
                  </span>{' '}
                  SSO {pluralize('login', socialLogins.length)}. You will need
                  to use your email and password next time you log in.
                </span>
              </div>
            </div>

            <FormLabel sx={{ marginBottom: socialLogins.length > 0 ? 1 : 2 }}>
              Enter your new email
            </FormLabel>
          </>
        )}

        <Input
          id={'email'}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          startDecorator={<Mail01Icon className="size-4" />}
        />
        {hasError && <FormHelperText>{errors.email?.message}</FormHelperText>}
      </FormControl>

      <div className="flex items-center gap-2 self-end">
        <Button
          type="button"
          variant="plain"
          color="neutral"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button type="submit" loading={isMutating || isUnlinkingSocialLogin}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

export function EditProfileModal(props: {
  className?: string;
  onSuccess?: (data: TenantSchema) => void;
  children?: (props: { onClick: () => void }) => ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const showEmailUpdateNotification = useShowEmailChange();

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          sx={{ width: 550, paddingX: 3, paddingY: 3 }}
        >
          {open &&
            (showEmailUpdateNotification ? (
              <EmailChangeNotification setOpen={setOpen} />
            ) : (
              <EmailEdit setOpen={setOpen} />
            ))}
          <ModalClose size="sm" sx={{ top: 24, right: 24 }} />
        </ModalDialog>
      </Modal>

      {props.children?.({
        onClick: () => {
          if (!props.disabled) {
            setOpen(true);
          }
        },
      })}
    </>
  );
}
